import { Trans } from '@lingui/react/macro';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { BrainDraftsReview } from '../components/brain-drafts-review.js';
import { CoachingDraftDrawer } from '../components/coaching-draft-drawer.js';
import { type FeedHomeInitial, FeedHomeView } from '../components/feed-home-view.js';
import { FollowupsSection } from '../components/followups-section.js';
import { NextActionsSection } from '../components/next-actions-section.js';
import { OnboardingChecklist } from '../components/onboarding-checklist.js';
import { PendingRecommendationsSection } from '../components/pending-recommendations-section.js';
import { PendingTakeoverProposalsSection } from '../components/pending-takeover-proposals-section.js';
import { TopNav } from '../components/top-nav.js';
import { TriageDrawer } from '../components/triage-drawer.js';
import type { Brand, MeResponse } from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import {
  brandBrainQuery,
  brandInputsQuery,
  brandOpenFollowupsQuery,
  brandsQuery,
  campaignsQuery,
  channelTimelineQuery,
  feedQuery,
  latestBrandMemoQuery,
  meQuery,
} from '../lib/queries.js';

/**
 * Per-brand home — `/brand/$slug/home`. 51/N reframe: /home and
 * /feed render the same `FeedHomeView` (three-column "index | hi
 * luis. | pulse" layout). The polaroid stack of
 * slot_approval_pending cards is mounted above NEEDS YOU. The
 * previous classic/agency split is gone; one home surface for
 * everyone.
 *
 * /home owns the URL search overlays (?draft / ?triage / ?card)
 * that other surfaces don't need; the rest of the layout is
 * delegated to FeedHomeView so the two routes stay visually
 * identical.
 *
 * Guards: meQuery + brand-access check via the parent layout
 * (brand.$slug.tsx).
 */
interface HomeSearch {
  /** [Layer 2] When set, /home renders the coaching-draft drawer
   *  overlay for the targeted recommendation id. Cleared by the
   *  drawer's close action. */
  draft?: string;
  /** [12.2L-178] When set, /home renders a triage drawer for the
   *  targeted rail section. Cleared by the drawer's close action. */
  triage?: 'recommendations' | 'next-actions' | 'brain-drafts' | 'takeover-proposals' | 'followups';
  /** When set, /home renders a single feed-card drawer overlay for
   *  the targeted card id. Lets briefing decision rows open inline
   *  instead of navigating to /feed. Cleared on close. */
  card?: string;
  /** Brand workbench drawer mode. Read by useBrandWorkbenchState
   *  off window.location, but declared here so TanStack typed
   *  navigation (legacy `_brand.*` route redirects, deep links
   *  from feed cards / agency-home CTAs) can pass it through
   *  without validateSearch stripping it. */
  brand?: 'mid' | 'full';
  /** Brand workbench active tab. Loose string — the workbench
   *  hook validates against BRAND_WORKBENCH_TABS itself. */
  brandTab?: string;
}

export const Route = createFileRoute('/brand/$slug/home')({
  validateSearch: (search: Record<string, unknown>): HomeSearch => {
    const out: HomeSearch = {};
    if (typeof search['draft'] === 'string' && search['draft'].length > 0) {
      out.draft = search['draft'];
    }
    const t = search['triage'];
    if (
      t === 'recommendations' ||
      t === 'next-actions' ||
      t === 'brain-drafts' ||
      t === 'takeover-proposals' ||
      t === 'followups'
    ) {
      out.triage = t;
    }
    if (search['brand'] === 'mid' || search['brand'] === 'full') {
      out.brand = search['brand'];
    }
    if (typeof search['brandTab'] === 'string' && search['brandTab'].length > 0) {
      out.brandTab = search['brandTab'];
    }
    if (typeof search['card'] === 'string' && search['card'].length > 0) {
      out.card = search['card'];
    }
    return out;
  },
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    // [designer-role] Designer-only users land on /designer instead
    // of the brand home — the home view's surfaces (campaigns,
    // analytics, channels) are all things they have no access to.
    if (me.designerOnly) throw redirect({ to: '/designer' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    // Warm caches that BOTH variants need. Classic mode mounts the
    // feed view at /home, so feed + campaign + brain envelopes must
    // be in cache before render to mirror /feed's first-paint speed.
    // s264 — also warm followups so the rail teaser doesn't flash on
    // first paint after we replaced the standalone /followups page.
    const [, , , feed, campaignList, envelope] = await Promise.all([
      queryClient.ensureQueryData(channelTimelineQuery(brand.id)),
      queryClient.ensureQueryData(brandInputsQuery(brand.id)),
      queryClient.ensureQueryData(latestBrandMemoQuery(brand.id)),
      queryClient.ensureQueryData(feedQuery(brand.id)),
      queryClient.ensureQueryData(campaignsQuery(brand.id)),
      queryClient.ensureQueryData(brandBrainQuery(brand.id)),
      queryClient.ensureQueryData(brandOpenFollowupsQuery(brand.id)),
    ]);
    return { brand, me, brands, feed, campaignList, envelope };
  },
  component: HomePage,
});

function HomePage() {
  const ctx = Route.useRouteContext();
  const { brand } = ctx;
  const search = Route.useSearch();

  // s216-54h.9/15 — onboarding-checklist gate per memory rule
  // (feedback_home_onboarding_gate). Until brand.onboardedAt is
  // stamped server-side, /home shows ONLY the OnboardingChecklist
  // + BrainDraftsReview. The full three-column dashboard reads as
  // confusing empty state on day one (pulse cards say "no spend
  // yet", "nothing shipped yet", "no events today" — reads as
  // failure rather than "not set up yet").
  //
  // 54h.15 fix: switched the gate from useOnboardingChecklistSettled
  // to brand.onboardedAt directly. The hook returns true on LIVE-
  // inferred settle, even before the server flag is stamped. That
  // unmounted the checklist the moment every step turned green —
  // BEFORE its internal useEffect could fire markBrandOnboarded.
  // mutation never ran → onboardedAt stayed null → BrandWorkbench's
  // `if (!brand.onboardedAt) return null;` gate (brand-workbench.tsx:27)
  // never opened. founder saw the dashboard but the workbench bubble
  // never appeared.
  //
  // gating on brand.onboardedAt keeps the checklist mounted through
  // its own celebrate window + mutation. when the server flag lands
  // + refreshBrands fires (54h.14), this gate flips along with
  // BrandWorkbench's gate in the same render.
  const isOnboarded = brand.onboardedAt !== null;

  // 51/N — single home surface. The previous classic/agency split is
  // gone; /home now always renders the feed view (the three-column
  // "index | hi luis. | pulse" layout) with the slot_approval_pending
  // polaroid stack injected above NEEDS YOU. SlotApprovalStack lives in
  // its own component (slot-approval-stack.tsx) and is consumed directly
  // by feed-home-view.tsx and here.
  const feedInitial: FeedHomeInitial = {
    me: ctx.me,
    envelope: ctx.envelope,
    feed: ctx.feed,
    brands: ctx.brands,
    campaignList: ctx.campaignList,
  };

  // s216-54h.13 — the gated → settled handoff used to hard-swap the
  // moment useOnboardingChecklistSettled flipped true. AnimatePresence
  // mode="wait" + per-branch motion.div lets the checklist view fade
  // up + out before the dashboard fades up + in, so the final step's
  // celebrate animation isn't cut short by the parent unmount and
  // the dashboard doesn't pop into existence cold.
  //
  // initial={false} skips animation on the FIRST mount (a settled
  // founder loading /home directly shouldn't see a fade-in), but
  // still animates the BETWEEN-state transitions. exit on the gated
  // branch: fade + lift up. entry on the settled branch: rise from
  // below + fade in.
  return (
    // [task 8142511] KEYED ON brand.id, and the key is the whole fix.
    //
    // mode="wait" caches the EXITING element — the one created on the previous
    // render, with the previous render's props — and keeps showing it until its
    // exit transition finishes. Switching from an un-onboarded brand to an
    // onboarded one flips isOnboarded, so the key goes 'gated' -> 'settled' and
    // AnimatePresence holds up the incoming branch while it finishes exiting
    // <GatedHome ctx={ctx} /> — still carrying the PREVIOUS BRAND's ctx.
    //
    // The founder saw exactly that: /brand/fiscalo/home, chip reading FISCALO,
    // and magig's onboarding checklist in the body listing magig.app. (The chip
    // was right because TopNav resolves from the activeBrandId store, which
    // top-nav.tsx sets before it navigates — it never reads route context.)
    //
    // Keying the motion.div CHILDREN on brand.id does not fix it: the key would
    // change, but it is still a child transition inside the same
    // AnimatePresence, so the old child still exits holding the old brand. The
    // remount has to happen HERE, above it — a new key unmounts the whole
    // AnimatePresence, exiting child included. initial={false} keeps the swap
    // instant, and the within-brand onboarding -> dashboard handoff below (the
    // reason mode="wait" exists at all) is untouched.
    <AnimatePresence key={brand.id} mode="wait" initial={false}>
      {!isOnboarded ? (
        <motion.div
          key="gated"
          // exit only — the gated branch has its OWN celebrate
          // animation (mag-checklist-celebrate, 1100ms) that runs
          // INSIDE this wrapper before exit fires. AnimatePresence
          // mode="wait" holds the dashboard back until exit
          // completes, giving the celebrate room to play out.
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.42, ease: [0.4, 0, 0.6, 1] }}
        >
          <GatedHome ctx={ctx} />
        </motion.div>
      ) : (
        <motion.div
          key="settled"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <FeedHomeView initial={feedInitial} brandSlug={brand.slug} />
          {/* [Layer 2] Coaching draft drawer — overlay activated by
          ?draft=<recId>. The drawer reads the rec out of the brain
          query and fires the coaching agent if no draft exists. */}
          {search.draft ? (
            <CoachingDraftDrawer brandId={brand.id} brandSlug={brand.slug} recId={search.draft} />
          ) : null}
          {/* [12.2L-178] Triage drawer — overlay activated by
          ?triage=<section>. Wraps the existing per-section
          triage component at full width so it has room to
          breathe (recs / next-actions / brain-drafts). */}
          {search.triage === 'recommendations' ? (
            <TriageDrawer section="recommendations" brandSlug={brand.slug}>
              <PendingRecommendationsSection brandId={brand.id} />
            </TriageDrawer>
          ) : null}
          {search.triage === 'next-actions' ? (
            <TriageDrawer section="next-actions" brandSlug={brand.slug}>
              <NextActionsSection brandId={brand.id} brandSlug={brand.slug} />
            </TriageDrawer>
          ) : null}
          {search.triage === 'brain-drafts' ? (
            <TriageDrawer section="brain-drafts" brandSlug={brand.slug}>
              <BrainDraftsReview brandId={brand.id} brandSlug={brand.slug} />
            </TriageDrawer>
          ) : null}
          {search.triage === 'takeover-proposals' ? (
            <TriageDrawer section="takeover-proposals" brandSlug={brand.slug}>
              <PendingTakeoverProposalsSection brandId={brand.id} brandSlug={brand.slug} />
            </TriageDrawer>
          ) : null}
          {search.triage === 'followups' ? (
            <TriageDrawer section="followups" brandSlug={brand.slug}>
              <FollowupsSection brandId={brand.id} brandSlug={brand.slug} />
            </TriageDrawer>
          ) : null}
          {/* FeedCardDrawer hoisted to brand.$slug.tsx layout in
          s216-54h.35 so notification deep-links work on every
          brand-scoped route, not just /home. */}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Gated home view — TopNav + "hi <name>." header + checklist +
// brain-drafts. Same outer shell as the settled FeedHomeView so the
// brand nav + greeting stay anchored across the handoff. Extracted
// into its own component so the parent's AnimatePresence has a
// stable JSX shape (single direct motion.div child per branch).
function GatedHome({ ctx }: { ctx: { brand: Brand; me: MeResponse } }) {
  const { brand } = ctx;
  const firstName = ctx.me.name.split(' ')[0]?.toLowerCase() ?? 'founder';
  // Built from two formats joined by the divider, rather than formatting
  // once and replacing a comma. The comma was an artifact of the old
  // hardcoded 'en-US' output ("Thu, Sep 17"); other locales put the
  // separator elsewhere or omit it, which would have left the label
  // without its divider — or eaten a comma that meant something else.
  const today = new Date();
  const dayLabel =
    `${formatDate(today, { weekday: 'short' })} · ${formatDate(today, { month: 'short', day: 'numeric' })}`.toUpperCase();
  return (
    <div className="flex h-[var(--app-h,100dvh)] flex-col overflow-hidden bg-[var(--cream)]">
      <TopNav active="home" />
      <main className="relative flex min-h-0 flex-1 flex-col">
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[820px] px-4 pb-32 pt-8 sm:px-6 lg:pb-12">
            <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              <Trans>↘ HOME · {dayLabel}</Trans>
            </p>
            <h1 className="mt-1 font-display text-[30px] font-black leading-[1.05] tracking-[-0.04em]">
              <Trans>hi {firstName}.</Trans>
            </h1>
            <div className="mt-8">
              <OnboardingChecklist brand={brand} />
            </div>
            {/* Brain-draft review used to sit inline here during onboarding,
            but it read as "an extra task" competing with the checklist + its
            full-card hover-lift was wrong for a non-clickable card. It now
            surfaces as a `brand_drafts_pending` feed card AFTER onboarding
            completes (server-side, ensureBrandDraftsPendingCards), opening the
            ?triage=brain-drafts drawer. */}
          </div>
        </div>
      </main>
    </div>
  );
}
